<template>
  <kendo-window
      ref="kendoWindowRef"
      :title="'IP 접근 제어 설정'"
      :modal="true"
      :width="'700px'"
      :visible="false"
      style="display:none;">
    <template>
      <div class="pop-container">
        <div class="pop-conts">
          <div class="small_title_wrap">
            <h2>허용/차단 IP 설정</h2>
          </div>

          <div class="ip-form">
            <!-- IP 정책 설정 -->
            <div class="ip-row">
              <label class="ip-label">IP 정책 설정<span class="forg">*</span></label>
              <div class="ip-field">
                <input
                    type="radio"
                    class="k-radio"
                    id="ipPolicy_Y"
                    value="Y"
                    v-model="draftIpPolicyYn">
                <label for="ipPolicy_Y" class="k-radio-label" style="margin-right:24px;">적용</label>
                <input
                    type="radio"
                    class="k-radio"
                    id="ipPolicy_N"
                    value="N"
                    v-model="draftIpPolicyYn">
                <label for="ipPolicy_N" class="k-radio-label">미적용</label>
              </div>
            </div>

            <!-- 허용 IP 설정 -->
            <div class="ip-row">
              <label class="ip-label">
                허용 IP 설정<span class="forg">*</span>
                <span class="ip-help" title="IP : 단일 IP 주소를 허용합니다.&#10;CIDR : 대역(예: 0~32)을 지정하여 허용합니다.">?</span>
              </label>
              <div class="ip-field">
                <div class="ip-type-row">
                  <span
                      v-for="item in ipTypeList"
                      :key="item.cdId"
                      style="margin-right:24px;">
                    <input
                        type="radio"
                        class="k-radio"
                        :id="`ipType_${item.cdId}`"
                        :value="item.cdId"
                        v-model="ipType"
                        :disabled="draftIpPolicyYn === 'N'">
                    <label :for="`ipType_${item.cdId}`" class="k-radio-label">{{ item.cdNm }}</label>
                  </span>
                </div>
                <div class="ip-input-row">
                  <template v-for="(octet, idx) in ipOctets">
                    <input
                        :key="`octet-${idx}`"
                        ref="octetInputs"
                        class="ip-octet"
                        maxlength="3"
                        v-model="ipOctets[idx]"
                        :disabled="draftIpPolicyYn === 'N'"
                        @input="onOctetInput(idx, $event)"
                        @keydown="onOctetKeydown(idx, $event)"
                        @paste="onOctetPaste($event)">
                    <span v-if="idx < 3" :key="`dot-${idx}`" class="ip-dot">.</span>
                  </template>
                  <template v-if="isCidr">
                    <span class="ip-dot">/</span>
                    <input class="ip-octet" maxlength="2" v-model="ipPrefix" :disabled="draftIpPolicyYn === 'N'">
                  </template>
                  <button
                      class="mid_btn ip-add-btn"
                      :disabled="draftIpPolicyYn === 'N'"
                      @click="addIp">추가</button>
                </div>
              </div>
            </div>

            <!-- 허용된 IP -->
            <div class="ip-row">
              <label class="ip-label">
                허용된 IP<span class="forg">*</span><br>
                <span class="ip-max-info">(최대 {{ maxIpCount }}개 허용)</span>
              </label>
              <div class="ip-field">
                <div class="ip-list-wrap">
                  <p v-if="draftIpList.length === 0" class="ip-empty">등록된 IP가 없습니다.</p>
                  <div v-for="(item, idx) in draftIpList" :key="idx" class="ip-list-item">
                    <span>{{ item.ipAddress }} / {{ item.cidrPrefix }}</span><span v-if="item.lastMdfyDt">{{ item.lastMdfyDt }}</span>
                    <button
                        class="mid_btn mgl5"
                        :disabled="draftIpPolicyYn === 'N'"
                        @click="removeIp(idx)">삭제</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="ip-guide">
            <p class="ip-guide-warn">'적용' 시 등록된 IP에서만 해당 사용자의 로그인이 허용됩니다.</p>
            <p>IP를 잘못 등록하면 로그인이 차단될 수 있으며, '미적용' 시에는 IP 제한 없이 로그인할 수 있습니다.</p>
            <p class="ip-current">(현재 접속 IP: {{ currentIp || '-' }})</p>
          </div>

          <div class="k-text-center mgt20">
            <button type="button" class="mid_btn mr5" @click="cancelIp">취소</button>
            <button type="button" class="mid_btn mr5" @click="resetIp">초기화</button>
            <button type="button" class="mid_btn orange" @click="applyIp">적용</button>
          </div>
        </div>
      </div>
    </template>
  </kendo-window>
</template>

<script>
import ApiConfig from '@/api/api.config'
import ApiUtil from '@/api/api.util'

export default {
  name: 'IpAccessControlWindow',
  props: {
    // 부모(UserSave)에 확정된 정책/IP 목록 (재오픈 시 초기값으로 사용)
    ipPolicyYn: {
      type: String,
      default: 'Y'
    },
    allowIpList: {
      type: Array,
      default: function () { return [] }
    },
    maxIpCount: {
      type: Number,
      default: 5
    }
  },
  data: function () {
    // 코드 그룹 51 : IP 유형 (IP / CIDR) - codeDtl 에서 조회
    const logisticsCd = this.$store.state.codeRequestStore.search.combobox.logisticsCd.data || []
    const ipTypeList = logisticsCd.filter(item => item.cdGrup === '51')
    return {
      draftIpPolicyYn: 'Y',
      draftIpList: [],
      ipTypeList: ipTypeList, // 코드 리스트 (cdId, cdNm)
      ipType: ipTypeList.length > 0 ? ipTypeList[0].cdId : '', // 선택된 cdId
      ipOctets: ['', '', '', ''],
      ipPrefix: '',
      currentIp: '',
    }
  },
  computed: {
    // 현재 선택된 IP 유형이 CIDR 인지 여부 (cdNm 에 'CIDR' 포함 여부로 판단)
    isCidr () {
      const sel = this.ipTypeList.find(item => item.cdId === this.ipType)
      return sel ? (sel.cdNm || '').toUpperCase().indexOf('CIDR') > -1 : false
    }
  },
  mounted () {
    this.fetchCurrentIp()
  },
  methods: {
    // 부모에서 호출 : 확정값을 임시값으로 복사 후 팝업 오픈
    open () {
      const vm = this
      vm.draftIpPolicyYn = vm.ipPolicyYn
      vm.draftIpList = vm.allowIpList.map(item => ({ ...item }))
      vm.ipType = vm.ipTypeList.length > 0 ? vm.ipTypeList[0].cdId : ''
      vm.ipOctets = ['', '', '', '']
      vm.ipPrefix = ''
      vm.$refs.kendoWindowRef.kendoWidget().center().open()
    },

    // 지정한 옥텟 입력칸으로 포커스 이동 후 전체 선택
    focusOctet (idx) {
      const vm = this
      const inputs = vm.$refs.octetInputs
      if (!inputs || !inputs[idx]) return
      vm.$nextTick(() => {
        inputs[idx].focus()
        inputs[idx].select()
      })
    },

    // 숫자만 허용 + 3자리 입력 시 다음 칸으로 자동 이동
    onOctetInput (idx, event) {
      const vm = this
      const raw = event.target.value || ''
      const val = raw.replace(/[^0-9]/g, '')
      if (val !== raw) {
        vm.$set(vm.ipOctets, idx, val)
      }
      if (val.length >= 3 && idx < 3) {
        vm.focusOctet(idx + 1)
      }
    },

    // 화살표키 / 백스페이스로 입력칸 이동
    onOctetKeydown (idx, event) {
      const vm = this
      const target = event.target
      const caret = target.selectionStart
      const len = (target.value || '').length

      if (event.key === 'ArrowLeft' && caret === 0 && idx > 0) {
        event.preventDefault()
        vm.focusOctet(idx - 1)
      } else if (event.key === 'ArrowRight' && caret === len && idx < 3) {
        event.preventDefault()
        vm.focusOctet(idx + 1)
      } else if (event.key === 'Backspace' && len === 0 && idx > 0) {
        event.preventDefault()
        vm.focusOctet(idx - 1)
      }
    },

    // IP 전체("192.168.0.1" 또는 "192.168.0.1/24")를 붙여넣으면 각 칸에 자동 분배
    onOctetPaste (event) {
      const vm = this
      const clip = event.clipboardData || window.clipboardData
      if (!clip) return
      const text = (clip.getData('text') || '').trim()
      // 점이 없으면 일반 붙여넣기(해당 칸에만 입력)로 처리
      if (text.indexOf('.') === -1) return
      event.preventDefault()

      const parts = text.split('/')
      const octets = parts[0].split('.')
      for (let i = 0; i < 4; i++) {
        const o = octets[i] !== undefined ? octets[i].replace(/[^0-9]/g, '').slice(0, 3) : ''
        vm.$set(vm.ipOctets, i, o)
      }
      if (vm.isCidr && parts[1] !== undefined) {
        vm.ipPrefix = parts[1].replace(/[^0-9]/g, '').slice(0, 2)
      }
      vm.focusOctet(3)
    },

    isValidOctet (val) {
      if (!/^\d{1,3}$/.test(val)) return false
      const n = Number(val)
      return n >= 0 && n <= 255
    },

    addIp () {
      const vm = this

      if (vm.draftIpPolicyYn === 'N') return

      if (vm.draftIpList.length >= vm.maxIpCount) {
        kendo.alert(`IP는 최대 ${vm.maxIpCount}개까지 등록할 수 있습니다.`)
        return
      }

      for (let i = 0; i < 4; i++) {
        if (!vm.isValidOctet((vm.ipOctets[i] || '').toString().trim())) {
          kendo.alert('IP 주소를 형식에 맞게 입력해주세요. (0~255)')
          return
        }
      }
      const ipAddress = vm.ipOctets.map(o => Number(o.toString().trim())).join('.')

      let cidrPrefix = 32
      if (vm.isCidr) {
        const prefix = (vm.ipPrefix || '').toString().trim()
        if (!/^\d{1,2}$/.test(prefix) || Number(prefix) < 0 || Number(prefix) > 32) {
          kendo.alert('CIDR 대역을 형식에 맞게 입력해주세요. (0~32)')
          return
        }
        cidrPrefix = Number(prefix)
      }

      // 중복 체크
      const exists = vm.draftIpList.some(item => item.ipAddress === ipAddress && item.cidrPrefix === cidrPrefix)
      if (exists) {
        kendo.alert('이미 등록된 IP입니다.')
        return
      }

      vm.draftIpList.push({
        ipAddress: ipAddress,
        cidrPrefix: cidrPrefix,
        ipType: vm.ipType,
      })

      // 입력값 초기화
      vm.ipOctets = ['', '', '', '']
      vm.ipPrefix = ''
    },

    removeIp (idx) {
      const vm = this
      if (vm.draftIpPolicyYn === 'N') return
      vm.draftIpList.splice(idx, 1)
    },

    resetIp () {
      const vm = this
      vm.draftIpPolicyYn = 'Y'
      vm.draftIpList = []
      vm.ipType = vm.ipTypeList.length > 0 ? vm.ipTypeList[0].cdId : ''
      vm.ipOctets = ['', '', '', '']
      vm.ipPrefix = ''
    },

    applyIp () {
      const vm = this

      if (vm.draftIpPolicyYn === 'Y' && vm.draftIpList.length === 0) {
        kendo.alert('IP 정책 적용 시 허용 IP를 1개 이상 등록해주세요.')
        return
      }

      // 확정값을 부모로 전달 (실제 저장은 등록 시점에 부모에서 수행)
      vm.$emit('apply', {
        ipPolicyYn: vm.draftIpPolicyYn,
        allowIpList: vm.draftIpList.map(item => ({ ...item }))
      })
      vm.$refs.kendoWindowRef.kendoWidget().close()
    },

    cancelIp () {
      const vm = this
      // 임시값 폐기 (확정값은 유지)
      vm.$refs.kendoWindowRef.kendoWidget().close()
    },

    fetchCurrentIp () {
      const vm = this
      const url = `${ApiConfig.eklDomain}/v1/ebmp/ai/manager/portal/user/getClientIp.json`
      ApiUtil.post(url, {}).then(response => {
        if (response.data && response.data.resultData && response.data.resultData.ip) {
          vm.currentIp = response.data.resultData.ip
        }
      }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.ip-form {
  padding: 4px 0 8px;
}
.ip-row {
  display: flex;
  align-items: flex-start;
  padding: 16px 4px;
  border-bottom: 1px solid #f0f0f0;
}
.ip-label {
  width: 140px;
  flex-shrink: 0;
  font-weight: bold;
  color: #333;
  padding-top: 6px;
  line-height: 1.4;
}
.ip-field {
  flex: 1;
  padding-top: 4px;
}
.ip-type-row {
  margin-bottom: 12px;
}
.ip-input-row {
  display: flex;
  align-items: center;
}
.ip-octet {
  width: 60px;
  height: 36px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-sizing: border-box;
}
.ip-octet:disabled {
  background: #f5f5f5;
}
.ip-dot {
  padding: 0 6px;
  font-weight: bold;
  color: #666;
}
.ip-add-btn {
  margin-left: 12px;
  background-color: #2A88C0;
  color: #ffffff;
}
.ip-help {
  display: inline-block;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 50%;
  background: #1f8ce6;
  color: #fff;
  font-size: 11px;
  font-weight: normal;
  cursor: help;
  margin-left: 2px;
}
.ip-max-info {
  color: #ec6941;
  font-weight: normal;
  font-size: 12px;
}
.ip-empty {
  color: #888;
  font-size: 13px;
  padding-top: 4px;
}
.ip-list-wrap {
  height: 130px;
  overflow-y: auto;
  padding-right: 4px;
}
.ip-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 360px;
  padding: 4px 0;
}
.ip-guide {
  margin-top: 16px;
  padding: 14px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  line-height: 1.6;
}
.ip-guide p {
  margin: 0;
}
.ip-guide-warn {
  color: #ec6941;
  font-weight: 700;
  margin-bottom: 4px !important;
}
.ip-current {
  margin-top: 6px;
  color: #1f8ce6;
}
</style>
